import { useState } from "react";
import { IconCopy, IconBrandPython, IconBrandJavascript } from "@tabler/icons-react";

interface CodeBlockProps {
    databaseId: string;
    searchType: "semantic" | "oasis";
}

const CodeBlock = ({ databaseId, searchType }: CodeBlockProps) => {
    const [activeTab, setActiveTab] = useState<"python" | "javascript">("python");
    const [copied, setCopied] = useState(false);

    const pythonCode = `import requests

# ${searchType.charAt(0).toUpperCase() + searchType.slice(1)} Search
response = requests.post(
    "https://api.thoughtfuloasis.com/api/database/${databaseId}/search/",
    headers={
        "X-Public-Key": "YOUR_PUBLIC_API_KEY",
        "X-Private-Key": "YOUR_PRIVATE_API_KEY"
    },
    json={
        "query": "your search query here",
        "type": "${searchType}",
        "limit": 15,
        "rerank": True
    }
)

results = response.json()
print(results)`;

    const javascriptCode = `// ${searchType.charAt(0).toUpperCase() + searchType.slice(1)} Search
const response = await fetch(
    "https://api.thoughtfuloasis.com/api/database/${databaseId}/search/",
    {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Public-Key": "YOUR_PUBLIC_API_KEY",
            "X-Private-Key": "YOUR_PRIVATE_API_KEY"
        },
        body: JSON.stringify({
            query: "your search query here",
            type: "${searchType}",
            limit: 15,
            rerank: true
        })
    }
);

const results = await response.json();
console.log(results);`;

    const copyToClipboard = async (code: string) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy code:", err);
        }
    };

    const currentCode = activeTab === "python" ? pythonCode : javascriptCode;

    return (
        <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            {/* Tab Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-gray-800 border-b border-gray-700">
                <div className="flex">
                    <button
                        onClick={() => setActiveTab("python")}
                        className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                            activeTab === "python"
                                ? "text-blue-400 border-b-2 border-blue-400"
                                : "text-gray-400 hover:text-gray-300"
                        }`}
                    >
                        <IconBrandPython size={14} className="sm:w-4 sm:h-4" />
                        <span>Python</span>
                    </button>
                    <button
                        onClick={() => setActiveTab("javascript")}
                        className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                            activeTab === "javascript"
                                ? "text-yellow-400 border-b-2 border-yellow-400"
                                : "text-gray-400 hover:text-gray-300"
                        }`}
                    >
                        <IconBrandJavascript size={14} className="sm:w-4 sm:h-4" />
                        <span>JavaScript</span>
                    </button>
                </div>
                
                {/* Copy Button */}
                <button
                    onClick={() => copyToClipboard(currentCode)}
                    className="flex items-center justify-center sm:justify-start space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-400 hover:text-gray-300 transition-colors border-t sm:border-t-0 border-gray-700 sm:border-t-0"
                >
                    <IconCopy size={14} className="sm:w-4 sm:h-4" />
                    <span>{copied ? "Copied!" : "Copy"}</span>
                </button>
            </div>

            {/* Code Content */}
            <div className="p-3 sm:p-4">
                <pre className="text-gray-300 font-mono text-xs sm:text-sm overflow-x-auto whitespace-pre">
                    <code>{currentCode}</code>
                </pre>
            </div>
        </div>
    );
};

export default CodeBlock; 